<i18n src="./resources/locales.json"></i18n>

<script>
import { mapGetters } from 'vuex';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';
import { MESSAGE_TYPE } from '@/constants/conversation/constants';
import { ELEMENT_STATE } from '@/constants/element/constants';

export default {
  name: 'EcsDesktopNotification',

  data() {
    return {
      desktopNotifications: []
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', ['getContactByEndPoint', 'getCrmContact', 'getContact']),

    ...mapGetters('ELEMENTS', ['getElement']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled'])
  },

  watch: {
    isOfflineModeEnabled(isEnabled) {
      const notification = {
        title: this.$t('connectivity.title'),
        body: isEnabled ? this.$t('connectivity.offlineModeEnabled') : this.$t('connectivity.offlineModeDisabled')
      };

      this.createNotification(notification);
    }
  },

  beforeDestroy() {
    this.desktopNotifications.forEach(notification => {
      notification.close();
    });
  },

  sockets: {
    INTERACTION_OFFERED_TO_AGENT_EVENT(event) {
      const interaction = new EcsInteraction(event);
      const {
        interactionMediaType,
        routing: { businessProcessName, origin }
      } = interaction;

      const mediaType = interactionMediaType.toLowerCase();

      let originator = origin;
      if (interaction.isInternal) {
        const contact = this.getContactByEndPoint(origin);
        originator = contact ? `${contact.name} (${contact.endPoint})` : origin;
      } else if (interaction.isConsultReceiver) {
        const contact = this.getContact(interaction.consultRequestor.id);
        originator = `${contact.name} (${contact.endPoint})`;
      } else if (interaction.customer) {
        originator = `${interaction.customer.name} (${
          interaction.isChat || interaction.isEmail ? interaction.customer.email : interaction.customer.endPoint
        })`;
      }

      const notification = {
        title: this.$t(`${mediaType}.interactionOffered.title`),
        body: this.$t(`${mediaType}.interactionOffered.content`, { businessProcessName, originator })
      };
      this.desktopNotifications.push(this.createNotification(notification));
    },

    INTERACTION_CONFERENCE_STARTED_EVENT(event) {
      const interaction = new EcsInteraction(event);
      const mediaType = interaction.interactionMediaType.toLowerCase();

      // NOTE: Patch for customer data :( Remove when ECS will fix state bug
      const customerIndex = interaction.connectedPartiesListNew.findIndex(contact => contact.partyType === 'Customer');
      if (customerIndex !== -1 && interaction.crm.contactId) {
        const contact = { id: interaction.crm.contactId, name: interaction.customerName };
        interaction.connectedPartiesListNew.splice(customerIndex, 1, this.getCrmContact(contact.id) || contact);
      }
      const participants = interaction.connectedPartiesListNew
        .filter(contact => contact.id !== this.loggedInAgent.id)
        .map(contact => contact.name.split(' ')[0]);

      const notification = {
        title: this.$t(`${mediaType}.conferenceStarted.title`),
        body: this.$t(`${mediaType}.conferenceStarted.content`, {
          participants: participants.reduce(
            (acc, cur, index, data) => acc + (index < data.length - 1 ? ', ' : ' and ') + cur
          )
        })
      };
      this.desktopNotifications.push(this.createNotification(notification));
    },

    ELEMENT_STATE_CHANGED_EVENT(event) {
      if (event.newElementStateId === ELEMENT_STATE.abandoned) {
        const element = this.getElement(event.elementId);
        if (!element) {
          return;
        }

        const mediaType = event.interactionMediaType.toLowerCase();

        const notification = {
          title: this.$t(`${mediaType}.abandoned.title`),
          body: this.$t(`${mediaType}.abandoned.content`, {
            name: (element.receptors && element.receptors[0].name) || ''
          })
        };
        this.desktopNotifications.push(this.createNotification(notification));
      }
    },

    CHAT_MESSAGE_STATUS_CHANGED_EVENT(event) {
      const { messageStatus, messageContent, originatorId, originatorName } = event;
      const originator = this.getContact(originatorId);

      if (
        messageStatus.toUpperCase() === MESSAGE_TYPE.regular &&
        !messageContent.startsWith('^!^%NotTyp') &&
        !messageContent.startsWith('^!^%Typing')
      ) {
        const notification = {
          title: this.$t('chat.incomingMessage.title'),
          body: this.$t('chat.incomingMessage.content', {
            name: originator ? originator.name : originatorName,
            content: messageContent
          })
        };
        this.desktopNotifications.push(this.createNotification(notification));
      }
    }
  },

  methods: {
    createNotification({ title, body }) {
      return new Notification(title, {
        body,
        icon: require('@/assets/xtiumcolor_x_mark.svg')
      });
    }
  },

  render() {
    return this.$scopedSlots.default({});
  }
};
</script>
